import React, { useEffect } from "react";
import { Facebook, Github, Mail, Twitter, Phone, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik as useFormic } from "formik";

// Image
import Logo from 'Common/Logo';
import { loginUser, socialLogin } from "slices/thunk";
import { useDispatch, useSelector } from "react-redux"; 
import withRouter from "Common/withRouter";
import { createSelector } from 'reselect';
import AuthIcon from "pages/AuthenticationInner/AuthIcon";
import { Link } from "react-router-dom";
import { getRoleFromAccessToken } from "helpers/jwt";

const Login = (props: any) => {

    document.title = "Login | Pari567 - Admin & Dashboard";

    const dispatch = useDispatch<any>();
    const navigate = useNavigate();

    const selectLogin = createSelector(
        (state: any) => state.Login, 
        (login) => ({
            loading: login.loading,
            error: login.error,
            isAuthenticated: login.isAuthenticated
        })
    )

    const { loading, error, isAuthenticated } = useSelector(selectLogin)

    const validation: any = useFormic({
        enableReinitialize: true,

        initialValues: {
            mobile_number: "",
            password: "",
            grant_type: "password"
        },
        validationSchema: Yup.object({
            mobile_number: Yup.string().required("Please Enter Your Mobile Number"),
            password: Yup.string().required("Please Enter Your Password"),
        }),
        onSubmit: async (values: any) => {
            try {
                await dispatch(loginUser(values));
                // If login is successful, the useEffect will handle navigation
            } catch (error) {
                // Error is already handled in the thunk
                console.error('Login submission error:', error);
            }
        }
    });

    const signIn = (type: any) => {
        dispatch(socialLogin(type, navigate))
    }

    const socialResponse = (type: any) => {
        signIn(type)
    }

// Redirect authenticated users to their role-appropriate landing page
useEffect(() => {
    if (isAuthenticated) {
        const role = getRoleFromAccessToken();
        const roleLower = (role || '').toLowerCase();

        if (roleLower === 'roll caller' || roleLower === 'roll_caller') {
            navigate('/players');
        } else {
            navigate('/dashboard');
        }
    }
}, [isAuthenticated, navigate]);

    React.useEffect(() => {
        const bodyElement = document.body;

        bodyElement.classList.add('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'lg:py-10', 'bg-slate-50', 'dark:bg-zink-800', 'dark:text-zink-100', 'font-public');

        return () => {
            bodyElement.classList.remove('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'lg:py-10', 'bg-slate-50', 'dark:bg-zink-800', 'dark:text-zink-100', 'font-public');
        }
    }, []);

    return (
        <React.Fragment>
            <div className="relative">
                <AuthIcon />

                <div className="mb-0 w-screen lg:mx-auto lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
                    <div className="!px-10 justify-center !py-12 card-body">
                        <Link to="/" className="flex justify-center mb-6">
                            <Logo size="lg" />
                        </Link>

                        <div className="mt-8 text-center">
                            <h4 className="mb-1 text-primary-500 dark:text-danger-500">Welcome Back !</h4>
                            <p className="text-slate-500 dark:text-zink-200">Sign in to continue to Pari567</p>
                        </div>

                        <form className="mt-10" id="signInForm"
                            onSubmit={(event: any) => {
                                event.preventDefault();
                                validation.handleSubmit();
                                return false;
                            }}>
                            {error && <div className="px-4 py-3 mb-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50" id="errorAlert">
                                <b>Login failed:</b> {error}
                            </div>}
                            <div className="mb-3">
                                <label htmlFor="mobile_number" className="inline-block mb-2 text-base font-medium">Mobile Number</label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="tel"
                                        id="mobile_number"
                                        name="mobile_number"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 pl-10"
                                        placeholder="Enter mobile number"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.mobile_number || ""}
                                    />
                                </div>
                                {validation.touched.mobile_number && validation.errors.mobile_number ? (
                                    <div id="mobile_number-error" className="mt-1 text-sm text-red-500">{validation.errors.mobile_number}</div>
                                ) : null}
                            </div>
                            
                            <div className="mb-3">
                                <label htmlFor="password" className="inline-block mb-2 text-base font-medium">Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 pl-10"
                                        placeholder="Enter password"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.password || ""}
                                    />
                                </div>
                                {validation.touched.password && validation.errors.password ? (
                                    <div id="password-error" className="mt-1 text-sm text-red-500">{validation.errors.password}</div>
                                ) : null}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <input id="checkboxDefault1" className="size-4 border rounded-sm appearance-none bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-blue-500 checked:border-custom-500 dark:checked:bg-blue-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400" type="checkbox" value="" />
                                    <label htmlFor="checkboxDefault1" className="inline-block text-base font-medium align-middle cursor-pointer">Remember me</label>
                                </div>
                            </div>
                            <div className="mt-10">
                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    className="w-full text-white btn bg-blue-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Signing In...' : 'Sign In'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default withRouter(Login);
